/**
 * Utilities for resolving static asset URLs via Scene7 with safe fallbacks.
 *
 * Behavior:
 * - Derive Scene7 ID from a public asset path by stripping directories and extensions
 * - Build a Scene7 URL using an env-configurable base
 * - Optionally check existence via HEAD on the server and cache results
 * - Provide a simple SSR-friendly resolver that returns either Scene7 or the original public path
 */

import { getENV } from './env';

const DEFAULT_SCENE7_STATIC_BASE = 'https://s7d2.scene7.com/is/image/PetSmart/';

/**
 * Returns the Scene7 static base URL from environment or default constant.
 */
function getScene7StaticBase(): string {
    return getENV('NEXT_PUBLIC_SCENE7_STATIC_BASE') ?? DEFAULT_SCENE7_STATIC_BASE;
}

/**
 * Extracts the Scene7 asset id from a public path by removing directories and the extension.
 * Examples:
 *  - "/icons/treats-new-lg.png" -> "treats-new-lg"
 *  - "assets/foo/bar.webp" -> "bar"
 */
export function buildScene7StaticId(publicPath: string): string {
    const path = publicPath.startsWith('/') ? publicPath.slice(1) : publicPath;
    const lastSlash = path.lastIndexOf('/');
    const file = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;
    const lastDot = file.lastIndexOf('.');
    return lastDot >= 0 ? file.slice(0, lastDot) : file;
}

/**
 * Builds a Scene7 URL for a given static asset id.
 */
export function getScene7StaticUrl(assetId: string): string {
    return `${getScene7StaticBase()}${assetId}`;
}
